import type { Dayjs } from 'dayjs';

/**
 * Heatmap cell states for the yearly plan-vs-real comparison. The diverging
 * scale encodes cumplimiento (real/plan): red below plan, green at/above.
 */
export type PlanVsRealCellState =
  | 'empty'
  | 'no-plan'
  | 'r4'
  | 'r3'
  | 'r2'
  | 'r1'
  | 'g1'
  | 'g2'
  | 'g3';

/** Parses a decimal money string to a finite number; NaN/undefined input maps to 0. */
export function parseMoney(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Map plan/real strings to a heatmap cell state.
 *
 * - plan=0 and real=0 -> 'empty' (no activity)
 * - plan=0 and real>0 -> 'no-plan' (invoiced without a planned amount)
 * - ratio real/plan: <0.25 'r4', <0.5 'r3', <0.75 'r2', <1 'r1',
 *   <1.25 'g1', <1.5 'g2', otherwise 'g3'
 */
/**
 * True when the cell date (year, month 1..12, dia, local time) is strictly
 * after `now` at the day level; the same day is not future. Pure: `now` is
 * injected so callers control the clock.
 */
export function isFutureDay(year: string, month: number, dia: number, now: Dayjs): boolean {
  const cell = now.year(Number(year)).month(month - 1).date(dia).startOf('day');
  return cell.isAfter(now.startOf('day'));
}

export function planVsRealCellState(plan: string, real: string): PlanVsRealCellState {
  const planNum = parseMoney(plan);
  const realNum = parseMoney(real);

  if (planNum === 0 && realNum === 0) return 'empty';
  if (planNum === 0) return 'no-plan';

  const ratio = realNum / planNum;
  if (ratio < 0.25) return 'r4';
  if (ratio < 0.5) return 'r3';
  if (ratio < 0.75) return 'r2';
  if (ratio < 1) return 'r1';
  if (ratio < 1.25) return 'g1';
  if (ratio < 1.5) return 'g2';
  return 'g3';
}
